import React, { useState, useMemo } from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'

import { useStyles } from './styles'
import { CommonAccordion, IAccordionData } from '../../shared/components/common-accordion'
import { CommonButton } from '../../shared/components/common-button'

export const FAQ: React.FC = () => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)

  const data: IAccordionData[] = useMemo(
    () => [
      {
        title: 'Who can use the FHB risk map?',
        details:
          'Anyone interested in knowing the FHB, FDK and DON risk levels in their area can use this map.  Using weather data across the Prairies, it provides a qualitative estimate of expected risk levels.  The risk level estimated for an area does not mean that every field in that area will experience such risk.  However, there is a good chance that the risk level indicated on the map for any crop on any specific day will match field observations.  Producers should conduct a field survey validation before making fungicide application decisions.'
      },
      {
        title: 'How do I use the FHB risk map?',
        details:
          'Steps on how the map works are provided on the homepage under “How it works” or by visiting the Map tab on the top right corner of the screen. '
      },
      {
        title: 'For what time period can I use the FHB risk map?',
        details:
          'The risk map is calculated for each day starting May 15 through to August 31.  Risk cannot be forecast beyond the current date.  The risk map uses weather data from the previous 2 weeks from more than 500 weather stations to calculate the risk level by location for a selected date.'
      },
      {
        title: 'Which provinces are supported?',
        details: 'Alberta, Saskatchewan and Manitoba'
      },
      {
        title: 'How accurate is the FHB risk map data?',
        details:
          'The models were developed from plot studies established at 15 locations across the three Canadian Prairie provinces – 5 locations at each Province and each location had 40 plots over a 3-year period. The models were validated at about 300 producer fields across the Prairies with accuracies ranging from 70 to 100%. '
      },
      {
        title: 'Do I have to pay to use the FHB risk map?',
        details:
          'The viewer is free to use. Funding for the research was provided through the Integrated Crop Agronomy Cluster Program, part of the Canadian Agricultural Partnership. The list of funding partners and weather data providers is on the home page.'
      },
      {
        title: 'How is the FHB risk calculated?',
        details:
          'FHB risk is calculated and updated each day from May 15 through Aug 31. On a given day, hourly weather data from 1:00 a.m. through midnight at each weather station for the previous 14-day period are used in the FHB risk models. The models were developed for Western Canada to generate risk values at each weather station. The FHB risk is then mapped out using a 10 km x 10 km grid for the prairie agricultural region. In each grid cell, the FHB risk is determined using inverse distance weighting from the risk values for the nearest 12 weather stations that are within 50 km of the grid cell.'
      },
      {
        title: 'Do the FHB risk maps for a specific date change after they are calculated?',
        details:
          'There may be times when weather data are delayed in being transmitted and this can create gaps in the hourly weather data needed at each station. If a weather station has any gap in its hourly data over the past 14 days, the station is not used in the FHB risk calculation. On each day, the FHB risk mapping tool acquires weather data going back 3 days, then recalculates the risk values for each station on all 3 days and then re-creates the maps for those days. By going back 3 days, some of the gaps in the hourly weather data can be filled in and more weather stations will be available for the inverse distance weighting at each grid cell on the maps. Thus, an FHB risk map for a given date may change slightly over the following 3-day period during which some gaps in the hourly weather data may be filled in.  The FHB risk maps for the current day are the ones that are most likely to change because the most current date is the one for which there is the greatest likelihood of experiencing missing hourly data.'
      },
      {
        title: 'What is the animation feature and how can it be used?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Once a risk map has been created, a slider bar appears at the bottom of the map. By clicking the Play
              button (<img alt={'Play button'} width={20} src={'/images/icons/fhb-play.png'} />
              ), the display will cycle through a time series of the same risk maps for the past week, so it is simple
              to see how the risk values have changed. By clicking the Stop button (
              <img alt={'Stop button'} width={20} src={'/images/icons/fhb-pause.png'} />
              ), the animation will pause at the map for the date on which the button was clicked. (Note, the export
              function works only with the original date selected and does not export any preceding maps shown in the
              animation. For example, if June 15 was selected to create a risk map, then the export function will export
              the June 15 map. If you are looking at the animation feature and click the stop function to see the map
              for June 12, the export function will still export the June 15 map because it was the map originally
              selected. To export the June 12 map, enter June 12 for the date in the lefthand panel and then click "Show
              Results". Then the June 12 map can be exported.)
            </Typography>

            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              The animation feature is included to show the recent changes in the various FHB risk types. When the risk
              level has been high or very high for several days, it increases the likelihood that the weather conditions
              have favored the development of the specific risk type in the particular crop if it is at a vulnerable
              stage (i.e. flowering stage for wheat, head emergence stage for barley). Likewise, low or moderate risk
              levels for several days decrease the likelihood that the weather conditions have favored the specific risk
              type when the crop is at a vulnerable stage. This is just one more perspective to consider and producers
              should still perform a field validation before making fungicide application decisions, since conditions
              for individual fields within a grid cell can vary.
            </Typography>
          </>
        )
      },
      {
        title: 'Why are the FHB risk levels different for different crops and risk types on the same day?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Each risk model for each crop and risk type was developed independently of the other risk models. In each
              case, the risk model selected for each crop and type of risk was based on its statistical accuracy as well
              as its simplicity (i.e. a model with 2 weather parameters was preferred over a model with similar accuracy
              but having 3 or more weather parameters). Therefore, the differences in the level of risk between risk
              types on a given day are completely a result of the variation in the different weather parameters for each
              risk model over the preceding period of time.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              The weather parameters used in each type of risk model (as of spring 2025) are below:
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Spring wheat FHB</em> - mean daily maximum relative humidity over the previous 14 days and the number
              of hours with temp between 25 and 28 in the previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Spring wheat FDK</em> - mean daily relative humidity over the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Winter wheat FHB</em> - mean daily relative humidity over the previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Barley FHB</em> - mean daily rainfall over the previous 14 days, mean daily temperature over the
              previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum FHB</em> - number of hours with both temp between 15 and 30 and relative humidity over 80% in
              the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum FDK</em> - number of hours with relative humidity over 80% in the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum DON</em> - mean daily relative humidity over the previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Across all crops and risk types, some type of relative humidity measurement preceding the flowering period
              is the most common and prevalent weather factor in the models. This is consistent with our epidemiological
              knowledge of the disease's life cycle. The barley FHB risk model is the only one that includes a rainfall
              parameter. Several of the risk models also include some type of air temperature parameter.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Previous research (<a href='http://hdl.handle.net/1993/36855'>http://hdl.handle.net/1993/36855</a>) has
              shown that the level of the FHB index during the filling stage (FHB risk) is not highly correlated to the
              levels of either FDK or DON. Therefore, the risk for FHB during the filling stage can be substantially
              different from either the FDK or DON risk.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Additional weather data and disease observations (both in research plots and producer fields) are being
              collected each year and added to a growing database. These data are reassessed and the models are updated
              with new weather parameters and coefficients as the increasing number of observations improves their
              statistical accuracy.
            </Typography>
          </>
        )
      }
    ],
    [classes]
  )

  return (
    <Container component={'section'} className={classes.wrapper}>
      <div className={classes.headerRow}>
        <Typography aria-level={1} variant='h3' component='h1'>
          Frequently Asked Questions
        </Typography>
        <div>
          <CommonButton onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Collapse' : 'Expand'} All
          </CommonButton>
        </div>
      </div>
      {data.map((item, index) => (
        <CommonAccordion capitalize={false} key={index} title={item.title} details={item.details} expanded={expanded}>
          {item.children}
        </CommonAccordion>
      ))}
    </Container>
  )
}
